/**
 * Composable for handling iOS keyboard behavior in modals and forms
 * Provides consistent iOS keyboard handling across all input components
 */

export const useIOSKeyboard = () => {
  // Detect if running on iOS
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent)

  /**
   * Handle input focus on iOS - scroll input into view to prevent keyboard overlay issues
   */
  const handleInputFocus = (event: FocusEvent) => {
    const target = event.target
    if (
      isIOS() &&
      (target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLElement)
    ) {
      // Scroll input into view on iOS to prevent keyboard overlay issues
      setTimeout(() => {
        try {
          ;(target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' })
        } catch {
          // ignore
        }
      }, 300) // Delay to allow keyboard animation
    }
  }

  /**
   * Handle input blur on iOS - reset viewport after keyboard dismissal
   */
  const handleInputBlur = () => {
    if (isIOS()) {
      // Force viewport reset after keyboard dismissal
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 300)
    }
  }

  /**
   * Get common input event handlers for iOS keyboard management
   */
  const getInputHandlers = () => ({
    onFocus: handleInputFocus,
    onBlur: handleInputBlur,
  })

  return {
    isIOS,
    handleInputFocus,
    handleInputBlur,
    getInputHandlers,
  }
}
